import React, { useState } from 'react';
import {
  Popover, Box, Typography, Button, CircularProgress,
  TextField, InputAdornment, Stack,
} from '@mui/material';
import { LocalOffer } from '@mui/icons-material';
import { useGetPosDiscountsQuery } from '../../../services/discountApi';
import DiscountList from './DiscountList';

const fmt = (v) => `₹${parseFloat(v || 0).toFixed(2)}`;

const scrollbar = {
  '&::-webkit-scrollbar': { width: '4px' },
  '&::-webkit-scrollbar-thumb': { bgcolor: '#CBD5E1', borderRadius: '10px' },
};

// Manual staff-override discount picker for a single exchange "new item" row.
// Deliberately a Popover, not the full DiscountDrawer — this is a single-field
// decision (pick one discount for one item) made while the cashier is still
// building the exchange, so it should not pull them into a separate screen.
//
// No "reason" field: unlike the normal cart discount flow, the exchange API
// does not accept/store a reason for this discount — showing one here would
// silently discard whatever the cashier typed, which is worse than not asking.
//
// No approval-request state: the backend has no manager-approval escalation
// for exchange discounts (an exchange completes as a single transaction with
// no "hold" state), so unlike DiscountDrawer this never shows "Submit for
// Approval" — an over-limit discount is simply rejected when the exchange is
// submitted, surfaced via the existing error alert in ExchangeCheckoutModal.
const ExchangeItemDiscountPopover = ({ anchorEl, item, onClose, onApply, onRemove }) => {
  const open = Boolean(anchorEl) && Boolean(item);
  const [selectedDiscount, setSelectedDiscount] = useState(null);
  const [customValue, setCustomValue] = useState('');

  const { data, isFetching } = useGetPosDiscountsQuery(
    { applicable_level: 'line_item', product_id: item?.product_id },
    { skip: !open }
  );
  const discounts = data?.data?.discounts || [];

  // No reset-on-open effect needed: MUI Popover unmounts its children when
  // closed (keepMounted defaults to false), so this component's local state
  // is naturally fresh every time it reopens — for the same item or a
  // different one.

  if (!item) return null;

  const itemSubtotal = parseFloat(item.retail_price || 0) * parseFloat(item.quantity || 1);
  const baseValue = parseFloat(selectedDiscount?.discount_value || 0);
  const isPct = selectedDiscount?.discount_type === 'percentage';
  const effective = customValue !== '' ? (parseFloat(customValue) || 0) : baseValue;
  const savings = selectedDiscount
    ? Math.min(isPct ? (itemSubtotal * effective / 100) : effective, itemSubtotal)
    : 0;

  const handleSelect = (d) => {
    setSelectedDiscount((prev) => (prev?.id === d.id ? null : d));
    setCustomValue('');
  };

  const handleApply = () => {
    if (!selectedDiscount) return;
    onApply({
      discount: selectedDiscount,
      modifiedValue: customValue !== '' ? parseFloat(customValue) : null,
    });
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{ sx: { width: 320, maxWidth: '92vw', borderRadius: '12px', mt: 0.5 } }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocalOffer sx={{ color: '#4F46E5', fontSize: 16, flexShrink: 0 }} />
        <Typography noWrap sx={{ fontSize: '0.8rem', fontWeight: 800, color: '#1E293B', flexGrow: 1 }}>
          {item.product_name}
        </Typography>
      </Box>

      {item.discount_id && (
        <Box sx={{ mx: 2, mt: 1.25 }}>
          <Box sx={{
            bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px',
            px: 1.5, py: 0.85, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}>
            <Typography noWrap sx={{ fontSize: '0.76rem', fontWeight: 700, color: '#166534', flexGrow: 1, minWidth: 0 }}>
              ✓ {item.discount_name} applied
            </Typography>
            <Button
              size="small"
              onClick={onRemove}
              sx={{ fontSize: '0.68rem', color: '#EF4444', textTransform: 'none', fontWeight: 700, minWidth: 0, px: 0.75, flexShrink: 0, '&:hover': { bgcolor: '#FEF2F2' } }}
            >
              Remove
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ maxHeight: 240, overflowY: 'auto', mt: 1, ...scrollbar }}>
        {isFetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} sx={{ color: '#4F46E5' }} />
          </Box>
        ) : (
          <DiscountList
            discounts={discounts}
            selectedId={selectedDiscount?.id}
            onSelect={handleSelect}
            emptyText="No discounts available for this item"
          />
        )}
      </Box>

      {selectedDiscount && (
        <Box sx={{ px: 2, pt: 1.25 }}>
          <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', px: 1.5, py: 1, mb: 1 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 600 }}>
                {isPct ? `${effective}% on ${fmt(itemSubtotal)}` : 'Flat off'}
              </Typography>
              <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#16A34A' }}>
                −{fmt(savings)}
              </Typography>
            </Stack>
          </Box>

          {selectedDiscount.allow_value_change && (
            <Box sx={{ mb: 0.5 }}>
              <TextField
                fullWidth size="small" type="number"
                value={customValue}
                onChange={(e) => setCustomValue(e.target.value)}
                placeholder={String(baseValue)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8', fontWeight: 600 }}>
                        {isPct ? '%' : '₹'}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: '#F8FAFC', fontSize: '0.85rem' } }}
              />
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ px: 2, py: 1.5, mt: 0.5, borderTop: '1px solid #F1F5F9', display: 'flex', gap: 1 }}>
        <Button
          fullWidth variant="outlined" onClick={onClose}
          sx={{ fontSize: '0.78rem', fontWeight: 700, textTransform: 'none', borderRadius: '8px', borderColor: '#E2E8F0', color: '#64748B' }}
        >
          Cancel
        </Button>
        <Button
          fullWidth variant="contained" disableElevation onClick={handleApply} disabled={!selectedDiscount}
          sx={{
            fontSize: '0.78rem', fontWeight: 800, textTransform: 'none', borderRadius: '8px',
            bgcolor: '#4F46E5', color: '#FFF',
            '&:hover': { bgcolor: '#4338CA' },
            '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
          }}
        >
          Apply
        </Button>
      </Box>
    </Popover>
  );
};

export default ExchangeItemDiscountPopover;
